/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */
import {
  Calculator,
  CheckCircle,
  Clock,
  DollarSign,
  FileText,
  Home,
  Layers,
  Ruler,
  Wrench,
  X,
} from "lucide-react";
import type { SolutionTemplateConfig } from "@/components/landing/solution-template";
import {
  CTA,
  AI_TOOL_COUNT,
  STARTING_PRICE,
} from "@/content/landing/constants";

export const residentialConstructionConfig: SolutionTemplateConfig = {
  tradeName: "Residential Construction",
  tradeSlug: "residential-construction",
  hero: {
    badge: "Residential Builder Platform",
    title: "Software for home builders",
    titleHighlight: "from lot to closing.",
    description:
      "What's the true cost of that custom home? Are your allowances realistic? Which phase is over budget? One platform for whole-house estimates, phase tracking, homeowner communication, and invoicing.",
    ctaText: CTA.PRIMARY,
    trustIndicators: [
      "7-day free trial",
      "No long-term contracts",
      "Phase-based tracking",
      "Cancel anytime",
    ],
  },
  trustBar: {
    headline: "Built for residential builders",
    proofPoints: [
      { icon: Home, text: "Custom homes, remodels, and spec builds" },
      { icon: DollarSign, text: "Phase-based budgeting and tracking" },
      { icon: FileText, text: "Client portal for homeowner updates" },
    ],
  },
  painPoints: {
    title: "Does this sound familiar?",
    description:
      "The daily reality of running a residential building business without a system.",
    items: [
      {
        icon: Clock,
        title: "Whole-house estimates that take a full week",
        description:
          "Foundation, framing, plumbing, electrical, HVAC, finish — every trade priced out. You spend a week building the estimate in a spreadsheet, then another day formatting the proposal.",
      },
      {
        icon: DollarSign,
        title: "Allowances that aren't tracked",
        description:
          "The homeowner hasn't picked tile, fixtures, or paint yet. You put in allowances — but when selections come in over budget, nobody sees it until the draw request.",
      },
      {
        icon: FileText,
        title: "Homeowners want to see progress",
        description:
          "It's a 6-month build. The homeowner calls every week asking for updates. You're managing their expectations through text messages and phone calls because there's no portal they can check.",
      },
    ],
  },
  stats: [
    { value: "Phase-based", label: "Estimate, track, and invoice by phase" },
    { value: `${AI_TOOL_COUNT} tools`, label: "AI assistant for your data" },
    { value: `$${STARTING_PRICE}/mo`, label: "Starting price" },
  ],
  howItWorks: {
    title: "The full lifecycle — one platform",
    description: "From house plans to final close-out.",
    steps: [
      {
        step: 1,
        title: "Set up your business",
        description:
          "Organize your costs by construction phase — foundation, framing, mechanical, finish. Set allowances for finishes the homeowner hasn't picked yet. Import sub pricing and configure your self-perform rates.",
        icon: Home,
      },
      {
        step: 2,
        title: "Win the work",
        description:
          "Upload architectural plans, measure from blueprints, and build work area by work area. Every trade priced out — from sitework to final clean. Allowance line items hold space for owner selections. Send a branded proposal with draw schedule.",
        icon: Calculator,
      },
      {
        step: 3,
        title: "Run the build",
        description:
          "Track budget vs. actuals across every phase. When the homeowner picks $12/sf tile instead of the $8 allowance, the budget updates immediately. Log costs, track time, manage change orders — all against the original estimate.",
        icon: Wrench,
      },
      {
        step: 4,
        title: "Keep the homeowner informed",
        description:
          "Client portal lets the homeowner view documents, track progress, and approve change orders. No more weekly phone calls — they can see where things stand. Invoice against the draw schedule as phases complete.",
        icon: FileText,
      },
    ],
  },
  features: {
    title: "What you actually get",
    description:
      "Not commercial construction software squeezed into residential. A platform that understands phases, allowances, and the homeowner relationship.",
    items: [
      {
        icon: Layers,
        title: "Phase-based work areas",
        description:
          "Organize your estimate by construction phase — foundation, framing, rough-in, mechanical, insulation, drywall, finish, landscaping. Each phase gets its own work area with separate budget tracking.",
        bullets: [
          "Phase-based organization",
          "Per-phase budget vs. actuals",
          "Sub bids and self-perform per phase",
          "Change orders tracked by phase",
        ],
      },
      {
        icon: DollarSign,
        title: "Allowances and selections",
        description:
          "Set allowance line items for finishes the homeowner hasn't chosen yet. When selections come in, update the line item — the budget adjusts automatically. No more surprise overages at draw time.",
        bullets: [
          "Allowance line items with placeholder amounts",
          "Update when selections are made",
          "Budget adjusts automatically",
          "Track selection overages",
        ],
      },
      {
        icon: FileText,
        title: "Client portal for homeowners",
        description:
          "Your homeowner gets their own portal — view proposals, track documents, see project progress, approve change orders. Reduce weekly phone calls and keep everyone on the same page.",
        bullets: [
          "Proposal viewing and approval",
          "Document sharing",
          "Change order approval",
          "Always-current project status",
        ],
      },
      {
        icon: Ruler,
        title: "Blueprint takeoffs for house plans",
        description:
          "Upload architectural plans and measure room areas, wall lengths, and material quantities. Feed measurements into your estimate through work packages organized by phase.",
        bullets: [
          "Measure from architectural plans",
          "Organize by room and phase",
          "Quantities link to estimate",
          "Multi-page plan support",
        ],
      },
    ],
  },
  beforeAfter: {
    title: "The old way vs. the CC way",
    before: [
      { icon: X, text: "Whole-house estimate takes a full week" },
      { icon: X, text: "Allowances tracked on a sticky note" },
      { icon: X, text: "Homeowner calls every week for updates" },
      { icon: X, text: "No idea which phase is over budget" },
      { icon: X, text: "Draw schedule on a spreadsheet" },
      { icon: X, text: "Change orders via text message" },
    ],
    after: [
      { icon: CheckCircle, text: "Phase-based estimates with work packages" },
      {
        icon: CheckCircle,
        text: "Allowances that update the budget when selections come in",
      },
      {
        icon: CheckCircle,
        text: "Client portal — homeowner checks status anytime",
      },
      { icon: CheckCircle, text: "Budget vs. actuals by phase in real time" },
      { icon: CheckCircle, text: "Invoicing against the draw schedule" },
      { icon: CheckCircle, text: "Change orders documented and tracked" },
    ],
  },
  roi: {
    title: "What changes when your business is connected",
    description:
      "When your whole-house estimate feeds into phase-based tracking, allowances update automatically, and the homeowner can see their project anytime.",
    metrics: [
      { value: "Organized", label: "Every trade, every phase, one estimate" },
      { value: "Transparent", label: "Homeowner portal reduces phone calls" },
      { value: "Visible", label: "Per-phase budget tracking in real time" },
    ],
    ctaText: CTA.PRIMARY,
  },
  faq: {
    title: "Questions residential builders ask",
    items: [
      {
        question: "Can I organize by construction phase?",
        answer:
          "Yes. Work areas map to phases — foundation, framing, mechanical, finish, etc. Each phase has its own budget, cost tracking, and line items.",
      },
      {
        question: "How do allowances work?",
        answer:
          "Create allowance line items with placeholder amounts. When the homeowner makes selections, update the line item. The estimate and project budget adjust automatically.",
      },
      {
        question: "Can the homeowner see progress?",
        answer:
          "Yes. The client portal lets homeowners view their proposal, see documents, and track project status. They approve or decline proposals and change orders through the portal.",
      },
      {
        question: "Does it handle draw schedules?",
        answer:
          "Invoice against phases as they complete. Line items carry over from the estimate so there's no re-entry.",
      },
      {
        question: "Can I track multiple projects at once?",
        answer:
          "Yes. Each project has its own detail page with budget tracking, cost logging, and timeline views. Dashboard gives you an overview across all active builds.",
      },
      {
        question: "Can my team use it?",
        answer:
          "Yes. Add PMs, estimators, or office staff. Role-based access. Division management on the Business plan.",
      },
    ],
  },
  cta: {
    title: "Build with confidence.",
    description:
      "Phase-based estimates, allowance tracking, homeowner portal, invoicing — connected. 7-day free trial.",
    ctaText: CTA.PRIMARY,
    trustIndicators: [
      "7-day free trial",
      "No long-term contracts",
      "Phase-based tracking",
      "Cancel anytime",
    ],
  },
};
